import type { ReviewAspectDto } from '@up2stay/up2promo-api-client';
import {
  SentimentPolarity,
  SentimentTrendGranularity,
} from '@up2stay/up2promo-api-client';
import {
  Badge,
  cn,
  healthAttentionFgClass,
  healthOkFgClass,
} from '@up2stay/up2promo-ui';
import { Star } from 'lucide-react';
import type { ReactNode } from 'react';

import {
  sentimentBadgeClass,
  starRatingFilledClass,
} from '@/core/components/reputation/reputation-chart-config';

type SentimentLabels = {
  positive: string;
  neutral: string;
  negative: string;
};

export type { SentimentTrendGranularity as TrendGranularity };

/**
 * Format a trend bucket's start date (YYYY-MM-DD) into a compact axis/tooltip
 * label. Daily/weekly buckets show day + month; monthly buckets show month +
 * year so multi-year spans don't collide on the same label.
 */
export function formatTrendLabel(
  dateStr: string,
  granularity: SentimentTrendGranularity,
  locale: string,
): string {
  const date = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return dateStr;
  const options: Intl.DateTimeFormatOptions =
    granularity === SentimentTrendGranularity.MONTH
      ? { month: 'short', year: '2-digit', timeZone: 'UTC' }
      : { day: 'numeric', month: 'short', timeZone: 'UTC' };
  return new Intl.DateTimeFormat(locale, options).format(date);
}

const SENTIMENT_POLARITIES = new Set<string>(Object.values(SentimentPolarity));

function sentimentClass(polarity: string): string {
  if (SENTIMENT_POLARITIES.has(polarity)) {
    return sentimentBadgeClass(polarity as SentimentPolarity);
  }
  return sentimentBadgeClass(SentimentPolarity.NEUTRAL);
}

/** Neutral metadata chips (topics, filter tags) — muted, not theme secondary. */
export const META_CHIP_CLASS =
  'border-transparent bg-muted/70 font-normal text-muted-foreground';

/** Subtle success chip for “already responded” states — `--ok-*` only. */
export const RESPONDED_CHIP_CLASS =
  'border-transparent bg-[color:var(--ok-bg)] font-normal text-[color:var(--ok-fg)]';

/** Stale / attention MetaChip — Signal Board attention via `health*`. */
export const HEALTH_ATTENTION_CHIP_CLASS = cn(
  'border-amber-500/20 bg-amber-500/10',
  healthAttentionFgClass,
);

/** Ahead / healthy MetaChip — `--ok-*` via `health*`. */
export const HEALTH_OK_CHIP_CLASS = cn(
  'border-[color:color-mix(in_oklab,var(--ok)_20%,var(--border))] bg-[color:var(--ok-bg)]',
  healthOkFgClass,
);

export function MetaChip({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <Badge variant="outline" className={cn(META_CHIP_CLASS, className)}>
      {children}
    </Badge>
  );
}

export function SentimentBadge({
  polarity,
  labels,
  className,
}: {
  polarity: string | null | undefined;
  labels: SentimentLabels;
  className?: string;
}) {
  if (!polarity) return null;
  const label =
    polarity === 'positive'
      ? labels.positive
      : polarity === 'negative'
        ? labels.negative
        : labels.neutral;
  return (
    <Badge
      variant="outline"
      className={cn(sentimentClass(polarity), className)}
    >
      {label}
    </Badge>
  );
}

export function StarRating({ rating }: { rating: number }) {
  const value = Math.min(5, Math.max(0, Math.round(rating)));
  const filledClass = starRatingFilledClass(value);
  return (
    <span
      className="inline-flex items-center gap-0.5"
      aria-label={`${value} / 5`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn(
            'h-3.5 w-3.5',
            i < value ? filledClass : 'fill-muted text-muted-foreground/35',
          )}
        />
      ))}
    </span>
  );
}

export function AspectChips({
  aspects,
  labels,
  max = 6,
  onSelect,
}: {
  aspects: ReviewAspectDto[];
  labels: SentimentLabels;
  max?: number;
  onSelect?: (key: string) => void;
}) {
  if (!aspects || aspects.length === 0) return null;
  const shown = aspects.slice(0, max);
  return (
    <div className="flex flex-wrap gap-1.5">
      {shown.map((aspect) => {
        const polarity = String(aspect.sentiment);
        return (
          <button
            key={aspect.key}
            type="button"
            disabled={!onSelect}
            onClick={onSelect ? () => onSelect(aspect.key) : undefined}
            className={cn(
              'rounded-full border px-2 py-0.5 text-xs font-medium transition-colors',
              sentimentClass(polarity),
              onSelect ? 'cursor-pointer hover:opacity-80' : 'cursor-default',
            )}
            title={labels[polarity as keyof SentimentLabels] ?? polarity}
          >
            {aspect.label}
          </button>
        );
      })}
    </div>
  );
}
